import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
  Sparkles, MapPin, CalendarDays, Wallet, Users, Heart, ArrowRight, ArrowLeft,
  Loader2, Lock, Download, Mail, Sun, Sunset, Moon, Clock, CheckCircle, Plane, Crown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = [
  "Food & Cuisine", "History & Culture", "Art & Museums", "Nature & Outdoors",
  "Shopping", "Nightlife", "Adventure", "Relaxation", "Family Fun", "Architecture",
];
const BUDGETS = ["Budget", "Mid-range", "Luxury"];
const TRAVEL_TYPES = ["Solo", "Couple", "Family", "Friends", "Business"];

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const PlannerNav = () => (
  <header className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-800 sticky top-0 z-50">
    <div className="container mx-auto px-4 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2" data-testid="planner-logo-link">
        <Plane className="h-8 w-8 text-sky-400" />
        <span className="text-2xl font-bold text-white">BMJ</span>
        <span className="text-sky-400 font-semibold">Flight Deals</span>
      </Link>
      <Link to="/" className="text-slate-300 hover:text-sky-400 text-sm transition-colors" data-testid="planner-home-link">
        Back to Flights
      </Link>
    </div>
  </header>
);

const SlotRow = ({ icon: Icon, label, slot }) => {
  if (!slot) return null;
  return (
    <div className="flex gap-3 py-3 border-b border-slate-700/60 last:border-0">
      <div className="mt-0.5"><Icon className="h-5 w-5 text-sky-400" /></div>
      <div className="flex-1">
        <p className="text-white font-medium">{label}: {slot.activity}</p>
        {slot.description && <p className="text-slate-400 text-sm mt-0.5">{slot.description}</p>}
        {slot.travel_time && (
          <p className="text-slate-500 text-xs mt-1 flex items-center gap-1">
            <Clock className="h-3 w-3" /> {slot.travel_time}
          </p>
        )}
      </div>
    </div>
  );
};

const DayCard = ({ day, locked }) => (
  <Card className={`bg-slate-800/70 border-slate-700 ${locked ? "relative overflow-hidden" : ""}`} data-testid={`itinerary-day-${day.day}`}>
    <CardContent className="p-5">
      <div className="flex items-center gap-3 mb-2">
        <span className="bg-sky-500 text-white text-sm font-bold rounded-full h-8 w-8 flex items-center justify-center">{day.day}</span>
        <h3 className="text-lg font-semibold text-white">{day.title}</h3>
      </div>
      <SlotRow icon={Sun} label="Morning" slot={day.morning} />
      <SlotRow icon={Sunset} label="Afternoon" slot={day.afternoon} />
      <SlotRow icon={Moon} label="Evening" slot={day.evening} />
      {day.approx_cost && <p className="text-slate-400 text-sm mt-3">Approx. cost: <span className="text-sky-400">{day.approx_cost}</span></p>}
    </CardContent>
  </Card>
);

const PricingCard = ({ title, price, symbol, period, highlight, onClick, loading, disabled, testid }) => (
  <div className={`rounded-xl border p-5 flex flex-col ${highlight ? "border-sky-500 bg-sky-500/10" : "border-slate-700 bg-slate-800/60"}`} data-testid={testid}>
    {highlight && <span className="text-xs font-semibold text-sky-400 flex items-center gap-1 mb-1"><Crown className="h-3 w-3" /> Best value</span>}
    <p className="text-white font-semibold">{title}</p>
    <p className="text-3xl font-bold text-white mt-2">{symbol}{price}<span className="text-sm text-slate-400 font-normal">{period}</span></p>
    <Button onClick={onClick} disabled={loading || disabled} className={`mt-4 ${highlight ? "bg-sky-500 hover:bg-sky-600" : "bg-slate-700 hover:bg-slate-600"} text-white`} data-testid={`${testid}-btn`}>
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock Now"}
    </Button>
  </div>
);

export default function Planner() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [settings, setSettings] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [payingPlan, setPayingPlan] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const [destination, setDestination] = useState("");
  const [days, setDays] = useState("3");
  const [startDate, setStartDate] = useState("");
  const [budget, setBudget] = useState("Mid-range");
  const [travelType, setTravelType] = useState("Couple");
  const [interests, setInterests] = useState([]);
  const [customerEmail, setCustomerEmail] = useState("");

  useEffect(() => {
    axios.get(`${API}/planner/settings`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Restore access after refresh / via magic link: /planner/:id?t=TOKEN
  useEffect(() => {
    const token = searchParams.get("t");
    if (routeId && token && !result) {
      axios.get(`${API}/planner/itinerary/${routeId}?t=${encodeURIComponent(token)}`)
        .then((r) => { setResult(r.data); setStep(3); })
        .catch(() => toast.error("This itinerary link is invalid or has expired."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  const persistUrl = (d) => {
    if (d?.id && d?.access_token) {
      navigate(`/planner/${d.id}?t=${encodeURIComponent(d.access_token)}`, { replace: true });
    }
  };

  const toggleInterest = (i) => {
    if (interests.includes(i)) {
      setInterests(interests.filter((x) => x !== i));
    } else if (interests.length >= 3) {
      toast.error("Pick up to 3 interests");
    } else {
      setInterests([...interests, i]);
    }
  };

  const handleGenerate = async () => {
    if (!destination.trim()) return toast.error("Please enter a destination");
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/planner/generate`, {
        destination: destination.trim(),
        days: parseInt(days),
        start_date: startDate || null,
        budget,
        travel_type: travelType,
        interests,
      });
      setResult(r.data);
      setStep(3);
      persistUrl(r.data);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate itinerary. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const refreshItinerary = useCallback(async (id, token) => {
    const r = await axios.get(`${API}/planner/itinerary/${id}${token ? `?t=${encodeURIComponent(token)}` : ""}`);
    setResult(r.data);
  }, []);

  const handlePay = async (plan) => {
    const isSubscription = plan === "monthly" || plan === "yearly";
    if (isSubscription && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim())) {
      toast.error("Enter a valid email to start a subscription");
      return;
    }
    setPayingPlan(plan);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); setPayingPlan(null); return; }

      let data, options;
      if (isSubscription) {
        ({ data } = await axios.post(`${API}/subscriptions/create`, {
          itinerary_id: result.id, plan, customer_email: customerEmail.trim(),
        }));
        options = {
          key: data.key_id,
          subscription_id: data.subscription_id,
          name: "BMJ Flight Deals",
          description: `Itinerary ${plan} subscription`,
          handler: async (resp) => {
            try {
              const vr = await axios.post(`${API}/subscriptions/verify`, {
                itinerary_id: result.id, plan,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_subscription_id: resp.razorpay_subscription_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success("Subscription active! Full itinerary unlocked.");
              setResult(vr.data);
              persistUrl(vr.data);
              window.scrollTo(0, 0);
            } catch {
              toast.error("Payment received but verification is pending. Contact support if it stays locked.");
            }
          },
          prefill: { email: customerEmail.trim() },
          theme: { color: "#0ea5e9" },
          modal: { ondismiss: () => { setPayingPlan(null); toast.message("Checkout cancelled"); } },
        };
      } else {
        ({ data } = await axios.post(`${API}/payments/create-order`, {
          itinerary_id: result.id, customer_email: customerEmail.trim() || null,
        }));
        options = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: "BMJ Flight Deals",
          description: "Single itinerary unlock",
          order_id: data.order_id,
          handler: async (resp) => {
            try {
              const vr = await axios.post(`${API}/payments/verify`, {
                itinerary_id: result.id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success("Payment successful! Full itinerary unlocked.");
              setResult(vr.data);
              persistUrl(vr.data);
              window.scrollTo(0, 0);
            } catch {
              toast.error("Payment could not be verified. Contact support.");
            }
          },
          prefill: customerEmail.trim() ? { email: customerEmail.trim() } : undefined,
          theme: { color: "#0ea5e9" },
          modal: { ondismiss: () => { setPayingPlan(null); toast.message("Checkout cancelled"); } },
        };
      }
      const rz = new window.Razorpay(options);
      rz.on("payment.failed", () => toast.error("Payment failed. Please try again."));
      rz.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment is not available right now.");
    } finally {
      setPayingPlan(null);
    }
  };

  const handleDownloadPdf = () => {
    window.open(`${API}/planner/itinerary/${result.id}/pdf?t=${encodeURIComponent(result.access_token)}`, "_blank");
  };

  const handleEmail = async () => {
    if (!emailInput.trim()) return toast.error("Enter your email");
    setEmailSending(true);
    try {
      await axios.post(`${API}/planner/itinerary/${result.id}/email?t=${encodeURIComponent(result.access_token)}`, { email: emailInput.trim(), app_origin: window.location.origin });
      toast.success(`Itinerary emailed to ${emailInput.trim()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email");
    } finally {
      setEmailSending(false);
    }
  };

  const sym = result?.currency_symbol || settings?.currency_symbol || "$";
  const resultDays = result ? result.days : [];
  const resultTips = result && result.tips ? result.tips : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <PlannerNav />
      <main className="container mx-auto px-4 py-10 max-w-3xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-sky-500/10 text-sky-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" /> AI Trip Planner
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">Plan Your Trip in Minutes with AI</h1>
          <p className="text-slate-300 text-base max-w-xl mx-auto">Answer a few quick questions and get a personalised day-wise sightseeing itinerary.</p>
        </div>

        {/* Progress */}
        {step < 3 && (
          <div className="flex items-center justify-center gap-2 mb-8" data-testid="planner-progress">
            {[1, 2].map((s) => (
              <div key={s} className={`h-2 rounded-full transition-all ${step >= s ? "bg-sky-500 w-16" : "bg-slate-700 w-10"}`} />
            ))}
          </div>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <Card className="bg-slate-800/70 border-slate-700">
            <CardContent className="p-6 space-y-5">
              <div>
                <Label className="text-slate-200 flex items-center gap-2 mb-2"><MapPin className="h-4 w-4 text-sky-400" /> Destination</Label>
                <Input data-testid="planner-destination-input" value={destination} onChange={(e) => setDestination(e.target.value)}
                  placeholder="e.g. Rome, Italy" className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-200 flex items-center gap-2 mb-2"><CalendarDays className="h-4 w-4 text-sky-400" /> Days (1-5)</Label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((d) => (
                      <button key={d} type="button" onClick={() => setDays(String(d))} data-testid={`planner-days-${d}`}
                        className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${days === String(d) ? "bg-sky-500 border-sky-500 text-white" : "border-slate-700 text-slate-300 hover:border-sky-500"}`}>{d}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-200 flex items-center gap-2 mb-2"><CalendarDays className="h-4 w-4 text-sky-400" /> Start date (optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" data-testid="planner-startdate-trigger"
                        className="w-full justify-start bg-slate-900 border-slate-700 text-slate-300 hover:text-white">
                        {startDate ? format(new Date(startDate), "PP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                      <CalendarComponent mode="single" selected={startDate ? new Date(startDate) : undefined}
                        onSelect={(d) => d && setStartDate(format(d, "yyyy-MM-dd"))} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <Button onClick={() => setStep(2)} className="w-full bg-sky-500 hover:bg-sky-600 text-white" data-testid="planner-next-btn">
                Next <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <Card className="bg-slate-800/70 border-slate-700">
            <CardContent className="p-6 space-y-6">
              <div>
                <Label className="text-slate-200 flex items-center gap-2 mb-3"><Wallet className="h-4 w-4 text-sky-400" /> Budget</Label>
                <div className="flex gap-2">
                  {BUDGETS.map((b) => (
                    <button key={b} type="button" onClick={() => setBudget(b)} data-testid={`planner-budget-${b}`}
                      className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${budget === b ? "bg-sky-500 border-sky-500 text-white" : "border-slate-700 text-slate-300 hover:border-sky-500"}`}>{b}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-slate-200 flex items-center gap-2 mb-3"><Users className="h-4 w-4 text-sky-400" /> Travelling as</Label>
                <div className="flex flex-wrap gap-2">
                  {TRAVEL_TYPES.map((t) => (
                    <button key={t} type="button" onClick={() => setTravelType(t)} data-testid={`planner-traveltype-${t}`}
                      className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${travelType === t ? "bg-sky-500 border-sky-500 text-white" : "border-slate-700 text-slate-300 hover:border-sky-500"}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-slate-200 flex items-center gap-2 mb-3"><Heart className="h-4 w-4 text-sky-400" /> Interests (up to 3)</Label>
                <div className="flex flex-wrap gap-2">
                  {INTERESTS.map((i) => (
                    <button key={i} type="button" onClick={() => toggleInterest(i)} data-testid={`planner-interest-${i}`}
                      className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${interests.includes(i) ? "bg-sky-500 border-sky-500 text-white" : "border-slate-700 text-slate-300 hover:border-sky-500"}`}>{i}</button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="border-slate-700 text-slate-300 hover:text-white" data-testid="planner-back-btn">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button onClick={handleGenerate} disabled={generating} className="flex-1 bg-sky-500 hover:bg-sky-600 text-white" data-testid="planner-generate-btn">
                  {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating your itinerary...</> : <><Sparkles className="mr-2 h-4 w-4" /> Create My Itinerary</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Result */}
        {step === 3 && result && (
          <div className="space-y-5" data-testid="planner-result">
            <Card className="bg-slate-800/70 border-slate-700">
              <CardContent className="p-5">
                <h2 className="text-2xl font-bold text-white">{result.destination}</h2>
                {result.summary && <p className="text-slate-300 mt-2">{result.summary}</p>}
                {result.best_time_to_visit && <p className="text-slate-400 text-sm mt-2">Best time to visit: {result.best_time_to_visit}</p>}
                <p className="text-sky-400 text-sm mt-3 font-medium">{result.total_days}-day itinerary{result.unlocked ? " · Unlocked" : ""}</p>
              </CardContent>
            </Card>

            {resultDays.map((d) => <DayCard key={d.day} day={d} locked={false} />)}

            {/* Paywall */}
            {!result.unlocked && result.locked_days > 0 && (
              <Card className="bg-slate-800/70 border-sky-500/40" data-testid="planner-paywall">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="h-5 w-5 text-sky-400" />
                    <h3 className="text-lg font-semibold text-white">Unlock all {result.total_days} days</h3>
                  </div>
                  <p className="text-slate-400 text-sm mb-5">{result.locked_days} more day(s), travel tips, PDF download and email delivery are locked. Choose a plan to unlock the full itinerary.</p>
                  <div className="mb-5">
                    <Label className="text-slate-300 text-sm flex items-center gap-2 mb-2"><Mail className="h-4 w-4 text-sky-400" /> Email for receipt & access <span className="text-slate-500">(required for subscriptions)</span></Label>
                    <Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="your@email.com"
                      className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="planner-customer-email" />
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <PricingCard title="Single Itinerary" price={result.prices.one_time} symbol={sym} period=" once" onClick={() => handlePay("one_time")} loading={payingPlan === "one_time"} disabled={payingPlan !== null} testid="plan-one-time" />
                    <PricingCard title="Monthly" price={result.prices.monthly} symbol={sym} period="/mo" highlight onClick={() => handlePay("monthly")} loading={payingPlan === "monthly"} disabled={payingPlan !== null} testid="plan-monthly" />
                    <PricingCard title="Yearly" price={result.prices.yearly} symbol={sym} period="/yr" onClick={() => handlePay("yearly")} loading={payingPlan === "yearly"} disabled={payingPlan !== null} testid="plan-yearly" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Unlocked extras */}
            {result.unlocked && (
              <Card className="bg-slate-800/70 border-emerald-500/40" data-testid="planner-unlocked">
                <CardContent className="p-6 space-y-4">
                  <p className="text-emerald-400 font-medium flex items-center gap-2"><CheckCircle className="h-5 w-5" /> Full itinerary unlocked</p>
                  {resultTips.length > 0 && (
                    <div>
                      <p className="text-white font-semibold mb-2">Travel Tips</p>
                      <ul className="list-disc list-inside text-slate-300 text-sm space-y-1">
                        {resultTips.map((t, i) => <li key={i}>{t}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <Button onClick={handleDownloadPdf} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="planner-download-pdf-btn">
                      <Download className="mr-2 h-4 w-4" /> Download PDF
                    </Button>
                    <div className="flex gap-2 flex-1">
                      <Input value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="your@email.com"
                        className="bg-slate-900 border-slate-700 text-white" data-testid="planner-email-input" />
                      <Button onClick={handleEmail} disabled={emailSending} variant="outline" className="border-slate-700 text-slate-200 hover:text-white" data-testid="planner-email-btn">
                        {emailSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-2 h-4 w-4" /> Email</>}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Upsells */}
            <Card className="bg-slate-800/40 border-slate-700">
              <CardContent className="p-5 grid sm:grid-cols-2 gap-3">
                <Link to="/contact" className="flex items-center justify-between rounded-lg border border-slate-700 p-4 hover:border-sky-500 transition-colors" data-testid="upsell-expert">
                  <span className="text-slate-200 text-sm font-medium">Talk to a travel expert</span>
                  <ArrowRight className="h-4 w-4 text-sky-400" />
                </Link>
                <Link to="/contact" className="flex items-center justify-between rounded-lg border border-slate-700 p-4 hover:border-sky-500 transition-colors" data-testid="upsell-visa">
                  <span className="text-slate-200 text-sm font-medium">Visa & booking assistance</span>
                  <ArrowRight className="h-4 w-4 text-sky-400" />
                </Link>
              </CardContent>
            </Card>

            <Button variant="ghost" onClick={() => { setResult(null); setStep(1); }} className="text-slate-400 hover:text-white w-full" data-testid="planner-restart-btn">
              Plan another trip
            </Button>

            <p className="text-slate-500 text-xs text-center leading-relaxed">
              Disclaimer: This itinerary is AI-generated for guidance only. Attraction availability, opening hours, prices and travel times are approximate and may change. BMJ Flight Deals makes no guarantees regarding bookings, pricing or visa requirements.
            </p>
          </div>
        )}
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
